import classNames from 'classnames';
import { omit } from 'lodash';
import { Scrollbars } from '@meitu/saas-ui';
import { clientInfo } from '@meitu/pc-bridge';
import { type TextEffectPreset } from '@meitu/saas-editor-core/es/layers/text-layer';
import { type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import { useEditor } from '@/hooks/use-editor';
import iconPRO from '../../assets/icon-pro.png';
import iconSVIP from '../../assets/icon-svip.png';
import iconVIP from '../../assets/icon-vip.png';
import { TextEffectDisplay } from '../../components';
import { getEffectPresets } from '../../config';
import { useLayerProps } from '../../hooks';
import { Tracker } from '../../utils';
import './base-effect-preset-panel.less';
import { TextLayerExtension } from '@meitu/saas-editor-core/es/editor/layer-extensions/text-layer';
import { TextEditPlugin } from '@meitu/saas-editor-core/es/plugins/text-edit';

type Props = {
    layerId: string;
    isIntl: boolean;
    recordId?: number;
    free?: boolean;
    applyEffectPresetHandler?: (effect: TextEffectPreset, index: number) => void;
    clearAllEffectsHandler?: () => void;
};

export const BaseEffectPresetPanel: React.FC<Props> = ({
    layerId,
    isIntl,
    recordId,
    free,
    applyEffectPresetHandler,
    clearAllEffectsHandler,
}) => {
    const editor = useEditor();

    const props = useLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        const { fontSize } = layerData;

        return {
            fontSize,
        };
    });

    const presets = getEffectPresets(props ? props.fontSize : 6);
    const previewPresets = getEffectPresets(170); // 用于预览列表需要传入固定字号，不受当前字号影响

    const applyEffectPreset = async (effect: TextEffectPreset, index: number) => {
        if (applyEffectPresetHandler) {
            applyEffectPresetHandler(effect, index);
            return;
        }
        await editor.getInstance(TextEditPlugin).exitEditing();

        editor.getInstance(TextLayerExtension).applyEffectPreset(layerId, free ? omit(effect, 'materialID') : effect);

        // eslint-disable-next-line react-hooks/rules-of-hooks
        Tracker.useTextEffectPreset({
            recordId,
            stageType: editor.stage.getType(),
            index,
        });
    };

    const clearAllEffects = async () => {
        if (clearAllEffectsHandler) {
            clearAllEffectsHandler();
            return;
        }
        await editor.getInstance(TextEditPlugin).exitEditing();
        editor.getInstance(TextLayerExtension).clearAllEffects(layerId);
    };

    const renderCornerIcon = () => {
        if (isIntl) {
            return <img className="base-effect-preset-panel__corner-icon" src={iconPRO} />;
        }
        return <img className="base-effect-preset-panel__corner-icon" src={clientInfo.isClient ? iconSVIP : iconVIP} />;
    };

    return (
        <Scrollbars>
            <div className="base-effect-preset-panel">
                <div
                    className={classNames('base-effect-preset-panel__item', {
                        'base-effect-preset-panel__item--cancel': true,
                    })}
                    onClick={clearAllEffects}
                >
                    <IconFont name="SlashCircleBold" fontSize={28} />
                </div>

                {presets.map((item, index) => {
                    const preview = previewPresets[index];
                    return (
                        <div
                            key={item.id}
                            className="base-effect-preset-panel__item"
                            onClick={() => applyEffectPreset(item, index)}
                        >
                            {item.isVip && !free ? renderCornerIcon() : null}
                            <TextEffectDisplay {...preview} />
                        </div>
                    );
                })}
            </div>
        </Scrollbars>
    );
};
